import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import PageContainer from 'components/@layout/PageContainer';
import Config from 'config';
import { useFormik } from 'formik';
import GET_ADMIN_USER_MANAGEMENT_USERS from 'graph/user/get-admin-user-management-users';
import useRouteAction from 'hooks/route-action/use-route-action';
import uniqBy from 'lodash/uniqBy';
import useStore from 'store';
import useProductCache from 'store/cache/product-cache';
import { ViewStyle } from 'store/types';
import { PrivilegedContext } from 'store/user';
import { ActionOption, ModuleProps } from 'types';
import { Organization, ProductType, User, UserOrganization } from 'types/schema-generated-types';
import FilterBar, { UserManagementListFilter } from './components/FilterBar';
import HeaderBar from './components/HeaderBar';
import ListViewContainer from './components/ListViewContainer';
import TileViewContainer from './components/TileViewContainer';
import EditUserDetailsModal from './modals/EditUserDetailsModal';
import InviteUserModal from './modals/InviteUserModal';
import ManageUserProductsModal from './modals/ManageUserProductsModal';
import UserDetailsModal from './modals/UserDetailsModal';

const { privilegesGroups } = Config;

const isIncludedOrganization = (orgIds: Set<number>, filter: UserManagementListFilter) => ({
  organizationId,
  organizationRoles
}: UserOrganization): boolean => {
  if (filter.organizationId && filter.organizationId !== organizationId) return false;
  if (!orgIds.has(organizationId)) return false;
  if (
    filter.organizationRoleClassification &&
    organizationRoles.every(r => r.classification !== filter.organizationRoleClassification)
  ) {
    return false;
  }

  return true;
};

const isIncludedByFilterFormValues = (
  filterForm: UserManagementListFilter,
  organizationIds: Set<number>
) => (acc: User[], { ...user }: User): User[] => {
  const { search, userStatus, productFamilyCode } = filterForm;

  if (
    search &&
    !`${user.firstName} ${user.lastName} ${user.username}`
      .toLowerCase()
      .includes(search.toLowerCase())
  )
    return acc;

  if (userStatus && user.status !== userStatus) return acc;

  const isOrgInContext = isIncludedOrganization(organizationIds, filterForm);
  const filteredOrgAssociations = user.organizationAssociations?.filter(isOrgInContext) ?? [];

  if (filteredOrgAssociations.length === 0) return acc;

  user.organizationAssociations = filteredOrgAssociations;

  if (
    productFamilyCode &&
    user.licenseUsage?.every(lu => lu.trial || lu.product?.family?.code !== productFamilyCode)
  ) {
    return acc;
  }

  return [...acc, user];
};

const AdminUserManagement: React.FC<ModuleProps> = props => {
  const { t } = useTranslation('AdminConsole');
  const [
    {
      ui: { viewStyle },
      userPrivilegesCache
    },
    { openFullModal, openHalfModal, openQuarterModal }
  ] = useStore();
  const [{ getPliProductFamiliesByType }] = useProductCache();
  const { data, loading } = useQuery(GET_ADMIN_USER_MANAGEMENT_USERS);

  const { values, handleChange, resetForm } = useFormik<UserManagementListFilter>({
    initialValues: {
      productFamilyCode: '',
      userStatus: '',
      organizationRoleClassification: '',
      organizationId: '',
      search: ''
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: (): void => {}
  });

  const openInviteUserModal = (): void => {
    openHalfModal({
      body: <InviteUserModal />,
      routeAction: 'invite-user'
    });
  };

  const openViewUserDetailsModal = (userId: number): void => {
    openQuarterModal({
      body: <UserDetailsModal />,
      routeAction: 'user-details',
      routeParams: { userId }
    });
  };

  const openEditUserDetailsModal = (userId: number): void => {
    openQuarterModal({
      body: <EditUserDetailsModal />,
      routeAction: 'edit-user-details',
      routeParams: { userId }
    });
  };

  const openManageUserProductsModal = (userId: number): void => {
    openFullModal({
      body: <ManageUserProductsModal />,
      routeAction: 'manage-user-products',
      routeParams: { userId }
    });
  };

  const handleViewUserDetailsClick = (event: React.MouseEvent<HTMLElement>): void => {
    openViewUserDetailsModal(+event.currentTarget.id);
  };

  const handleEditUserDetailsClick = (event: React.MouseEvent<HTMLElement>): void => {
    openEditUserDetailsModal(+event.currentTarget.id);
  };

  const handleManageUserProductsClick = (event: React.MouseEvent<HTMLElement>): void => {
    openManageUserProductsModal(+event.currentTarget.id);
  };

  useRouteAction(({ action, params }) => {
    if (!action) return;
    switch (action) {
      case 'invite-user':
        return openInviteUserModal();
      case 'user-details':
        return openViewUserDetailsModal(params.userId);
      case 'edit-user-details':
        return openEditUserDetailsModal(params.userId);
      case 'manage-user-products':
        return openManageUserProductsModal(params.userId);
      default:
        return null;
    }
  });

  const organizations: Organization[] = useMemo(() => data?.organizations ?? [], [data]);

  const filteredUsers = useMemo(() => {
    if (!data?.users) return [];

    const organizationIds = organizations.reduce((acc, a) => acc.add(a.id), new Set<number>());
    const users: User[] = data.users.reduce(
      isIncludedByFilterFormValues(values, organizationIds),
      [] as User[]
    );

    return uniqBy(users, 'id').sort((a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    );
  }, [data, organizations, values]);

  const actionOptions: ActionOption[] = [
    {
      label: t('View User Details'),
      MenuItemProps: { onClick: handleViewUserDetailsClick }
    },
    {
      label: t('Edit User Details'),
      MenuItemProps: { onClick: handleEditUserDetailsClick },
      isPrivileged: ({ organizationId }: PrivilegedContext) =>
        userPrivilegesCache.isPrivileged(privilegesGroups.mutateUser, {
          organizationId
        })
    },
    {
      label: t('Manage User Products'),
      MenuItemProps: { onClick: handleManageUserProductsClick },
      isPrivileged: ({ organizationId }: PrivilegedContext) =>
        userPrivilegesCache.isPrivileged(privilegesGroups.updateOrgLicense, {
          organizationId
        })
    }
  ];

  return (
    <PageContainer loading={loading}>
      <HeaderBar {...props} onAddClick={openInviteUserModal} />
      <FilterBar
        values={values}
        handleChange={handleChange}
        resetForm={resetForm}
        organizations={organizations}
        productFamilies={getPliProductFamiliesByType(ProductType.USER)}
      />
      {viewStyle === ViewStyle.TILE ? (
        <TileViewContainer users={filteredUsers} actionOptions={actionOptions} />
      ) : (
        <ListViewContainer users={filteredUsers} actionOptions={actionOptions} />
      )}
    </PageContainer>
  );
};

export default AdminUserManagement;
